import {
  CircularProgress,
  DialogTitle,
  Divider,
  Stack,
  Box,
  Typography,
} from "@mui/material";
import {
  CloseIconWrapper,
  CreateBoardWrapper,
  FontGrayNormal,
  ModalIconWrapper,
  ModalTitle,
  StyledDialog,
} from "./style";
import { CloseIcon, CreateFolderIcon } from "../../assets/Icons";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ErrorMessage } from "@hookform/error-message";
import {
  isOpenPurchaseSeatSelector,
  seatSeactionLoaderSelector,
} from "../../store/metadata/selector";
import { useTheme } from "styled-components";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import { StyledOutlinedInput } from "../commonComponents/formFields/style";
import { CustomButton } from "../../globalStyle";
import { useEffect, useState } from "react";
import {
  // activeSubscriptionSelector,
  isButtonLoadingSelector,
  userCardDetailsSelector,
} from "../../store/payment/selector";
import { PaymentSagaActions } from "../../store/payment/sagas";
import { useNavigate } from "react-router-dom";
import routeConstant from "../../utils/routeConstant";

type PurchaseSeatFormValues = {
  seatCount: number;
};

const purchaseSeatValidationSchema = yup.object().shape({
  seatCount: yup
    .number()
    .typeError("Seat count must be a number")
    .required("Number of seats is required")
    .min(1, "At least 1 seat is required")
    .max(10, "Maximum 10 seats can be purchased at once"),
});

const PurchaseNewSeatModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const openModal = useSelector(isOpenPurchaseSeatSelector);
  const isButtonloading = useSelector(isButtonLoadingSelector);
  const seatSeactionLoader = useSelector(seatSeactionLoaderSelector);
  const userCardDetails = useSelector(userCardDetailsSelector);
  const theme = useTheme();
  // const activePlan = useSelector(activeSubscriptionSelector);

  const [seatSummary, setSeatSummary] = useState<{
    seatCount: number;
    totalAmount: number;
  } | null>(null);
  const PER_SEAT_PRICE = 9.99;
    // activePlan?.activePlanDetails?.planType === "Monthly" ? 9.99 : 119.88;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PurchaseSeatFormValues>({
    resolver: yupResolver(purchaseSeatValidationSchema),
    mode: "onTouched",
  });

  useEffect(() => {
    dispatch(PaymentSagaActions.GetUserCardDetails());
  }, [dispatch]);

  const handleClose = () => {
    dispatch(MetaDataReducerAction.setOpenPurchaseSeatModal(false));
    reset();
    setSeatSummary(null);
  };

  const onSubmit = (data: PurchaseSeatFormValues) => {
    const seatCount = data.seatCount;
    return new Promise<void>((resolve, reject) => {
      dispatch(
        MetaDataSagaActions.purchseSeatAmountDetails({
          seatCount,
          resolve,
          reject,
        })
      );
    })
      .then((response: any) => {
        setSeatSummary({
          seatCount,
          totalAmount: response?.amount,
        });
      })
      .catch((error) => {
        console.error("Purchase failed:", error);
      });
  };

  return (
    <StyledDialog
      open={openModal}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ padding: "1.875rem 1.875rem 1.25rem" }}>
        <Stack direction="row" gap="0.75rem" alignItems="start">
          <ModalIconWrapper>
            <CreateFolderIcon fill="white" />
          </ModalIconWrapper>
          <Stack>
            <ModalTitle>Purchase New Seat</ModalTitle>
            <FontGrayNormal>
              Enter the number of additional seats you'd like to purchase.
            </FontGrayNormal>
          </Stack>
        </Stack>
        <CloseIconWrapper onClick={handleClose}>
          <CloseIcon />
        </CloseIconWrapper>
      </DialogTitle>

      <Divider sx={{ borderColor: "#E3E6EA" }} />

      <CreateBoardWrapper>
        <Stack spacing={2}>
          <Box display="flex" gap={2} alignItems="center">
            <StyledOutlinedInput
              sx={{
                flex: 1,
                borderRadius: "1.688rem",
                color: `${theme.colors.gray1} !important`,
                border: `1px solid ${theme.colors.border1}`,
              }}
              type="number"
              placeholder="Enter number of seats"
              {...register("seatCount")}
              inputProps={{
                min: 1,
                max: 10,
              }}
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const value = e.target.value;
                const numValue = Number(value);
                if (value === '' || (numValue >= 1 && numValue <= 10 && !value.includes('.'))) {
                  e.target.value = value;
                } else {
                  e.target.value = e.target.value.slice(0, -1);
                }
              }}
            />
            <CustomButton
              type="button"
              disabled={seatSeactionLoader?.loading}
              onClick={handleSubmit(onSubmit)}
              sx={{
                height: "3.375rem",
                paddingInline: "1.031rem 0.875rem",
                borderRadius: "1.688rem",
                border: `1px solid ${theme.colors.border1}`,
                background: "#FFFFFF",
                color: `${theme.colors.gray1}`,
              }}
              startIcon={
                seatSeactionLoader?.loading && (
                  <CircularProgress size={20} color="inherit" />
                )
              }
            >
              Purchase
            </CustomButton>
          </Box>

          <ErrorMessage
            name="seatCount"
            errors={errors}
            as="p"
            style={{
              color: "red",
              fontSize: "0.8rem",
              fontWeight: "bold",
              marginLeft: "0.5rem",
            }}
          />

          <Typography
            sx={{
              fontSize: "1rem",
              color: theme.colors.gray1,
              mt: 1,
            }}
          >
            Each additional seat is pro-rated at ${PER_SEAT_PRICE}/mo.
          </Typography>

          {seatSummary && (
            <Box mt={2}>
              <Box
                p={2}
                border={`1px solid ${theme.colors.border1}`}
                borderRadius="12px"
                width="100%"
                maxWidth="360px"
              >
                <Typography fontWeight={600}>Purchase Summary:</Typography>
                <Typography mt={1}>
                  Number of Seats: {seatSummary.seatCount}
                </Typography>
                <Typography>Total: ${seatSummary.totalAmount}</Typography>
              </Box>

              <Box mt={2} gap={2} display="flex" flexDirection="column">
                {userCardDetails?.length ? (
                  <CustomButton
                    onClick={() => {
                      navigate(routeConstant?.choosePaymentCard, {
                        state: { seatSummary },
                      });
                      dispatch(
                        MetaDataReducerAction.setOpenPurchaseSeatModal(false)
                      );
                    }}
                    disabled={isButtonloading}
                    sx={{
                      fontSize: "16px",
                      color: "white",
                      borderRadius: "25px",
                      background:
                        "linear-gradient(94.04deg, #1c75bc 2.44%, #00aeef 112.73%)",
                      padding: "14px 30px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {isButtonloading && (
                      <CircularProgress
                        size={18}
                        color="inherit"
                        sx={{ mr: 1 }}
                      />
                    )}
                    Checkout with Saved Card
                  </CustomButton>
                ) : null}
                <CustomButton
                  style={{
                    fontSize: "16px",
                    color: "white",
                    borderRadius: "25px",
                    background:
                      "linear-gradient(94.04deg, #1c75bc 2.44%, #00aeef 112.73%)",
                    padding: "14px 30px",
                    height: "38px",
                  }}
                  onClick={() => {
                    navigate(
                      `${routeConstant?.paymentCheckout}/dropin/seatCheckout`,
                      {
                        state: {
                          seatCount: seatSummary.seatCount,
                          isSeat: true,
                        },
                      }
                    );
                    dispatch(
                      MetaDataReducerAction.setOpenPurchaseSeatModal(false)
                    );
                  }}
                >
                  Checkout with New Card
                </CustomButton>
              </Box>
            </Box>
          )}
        </Stack>
      </CreateBoardWrapper>
    </StyledDialog>
  );
};

export default PurchaseNewSeatModal;
